import React, { useId } from 'react';
import { LEAD_OPTIONS, scheduleWarnings } from './reminderSchedule';

const whenLabel = (row, survey) => {
  if (row.early) return survey ? 'At or before sending' : 'Before request';
  return row.late ? `Day ${row.day} · ${survey ? 'after closing' : 'after start'}` : `Day ${row.day}`;
};

/**
 * The reminder schedule drawn to scale for an example request made `lead`
 * days before the time off. The drawn track is decorative; the list under it
 * says the same thing for screen readers and replaces it on narrow screens.
 */
export default function ReminderTimeline({ rows, lead, onLeadChange, survey = false }) {
  const headingId = useId();
  const span = Math.max(lead, ...rows.map((row) => row.day), 8) + 1;
  const pct = (day) => `${(Math.max(0, day) / span) * 100}%`;
  const warning = survey
    ? [
      ...(rows.some((row) => row.early) ? ['Reminders at or before the initial survey email are skipped.'] : []),
      ...(rows.some((row) => row.late) ? ['Reminders after the closing day are not sent.'] : []),
    ].join(' ')
    : scheduleWarnings(rows, lead);

  // Sorted by day, with the first day off in its place.
  const listItems = [
    ...rows.map((row) => ({ key: row.id, day: row.day, row })),
    { key: 'start', day: lead - 0.5, row: null },
  ].sort((a, b) => a.day - b.day);

  return (
    <section className="ar-card ar-schedule" aria-labelledby={headingId}>
      <div className="ar-schedule__head">
        <h3 id={headingId} className="ar-card__title">Schedule</h3>
        <div className="ar-lead">
          {survey ? 'Example: survey closes' : 'Example: time off starts'}
          <div className="ar-seg" role="radiogroup" aria-label="Example notice period">
            {LEAD_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={value === lead}
                onClick={() => onLeadChange(value)}
              >
                {value} days
              </button>
            ))}
          </div>
          {survey ? 'after it is sent' : 'after the request'}
        </div>
      </div>

      <div className="ar-track-wrap">
        <div className="ar-track" aria-hidden="true">
          <div className="ar-rail" />
          <div className="ar-rail-fill" style={{ width: pct(lead) }} />
          {Array.from({ length: span + 1 }, (_, day) => (
            <div key={day} className={`ar-tick${day % 7 === 0 ? ' ar-tick--week' : ''}`} style={{ left: pct(day) }} />
          ))}
          <div className="ar-start">{survey ? 'Survey sent' : 'Request submitted'}</div>
          <div className="ar-end" style={{ left: pct(lead) }}>
            <span className="ar-flag">{survey ? 'Survey closes' : 'Time off starts'} · day {lead}</span>
            <span className="ar-line" />
          </div>
          {rows.map((row) => (
            <div
              key={row.id}
              className={`ar-marker${row.late || row.early ? ' ar-marker--off' : ''}`}
              style={{ left: pct(row.day) }}
            >
              <span className="ar-dot"><i className="bi bi-envelope" /></span>
              <span className="ar-marker__name">Reminder {row.index + 1}</span>
              <span className="ar-marker__when">{whenLabel(row, survey)}</span>
            </div>
          ))}
        </div>

        <ol className="ar-track-list" aria-label="Send schedule">
          {listItems.map(({ key, row }) => (row ? (
            <li key={key} className={row.late || row.early ? 'is-off' : ''}>
              <span>Reminder {row.index + 1}</span>
              <span>{whenLabel(row, survey)}</span>
            </li>
          ) : (
            <li key={key} className="is-start">
              <span>{survey ? 'Survey closes' : 'Time off starts'}</span>
              <span>Day {lead}</span>
            </li>
          )))}
        </ol>
      </div>

      {warning ? (
        <div className="ar-warning" role="status">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{warning}</span>
        </div>
      ) : null}
    </section>
  );
}
